/**
 *
 * VerifyBank
 *
 */
import React, { forwardRef, memo, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import rupee from './assets/rupee.svg';
import percentage from './assets/percentage.svg';
import { media } from 'styles/media';
import { useAdvisor } from 'app/Provider/AdvisorProvider';
interface Props {
  onsubmit: (data: any) => void;
}
const validationSchema = Yup.object().shape({
  // Checkbox fields
  charge_free: Yup.boolean(),
  charge_aum: Yup.boolean(),
  flat_fee_box: Yup.boolean(),
  hourly_box: Yup.boolean(),

  // At least one option must be selected
  anyOptionSelected: Yup.boolean().test(
    'at-least-one',
    'Please select at least one subscription plan',
    function () {
      const { charge_free, charge_aum, flat_fee_box, hourly_box } = this.parent;
      return charge_free || charge_aum || flat_fee_box || hourly_box;
    },
  ),

  fee_percentage: Yup.number()
    .when('charge_aum', {
      is: true,
      then: schema =>
        schema
          .required('Fee percentage is required')
          .min(1, 'Must be at least 1'),
      otherwise: schema => schema,
    })
    .typeError('Must be a number')
    .integer('Must be an integer')
    .max(100, 'Percentage cannot exceed 100'),

  minimum_fee: Yup.number()
    .when('charge_aum', {
      is: true,
      then: schema =>
        schema.required('Minimum fee is required').min(1, 'Must be at least 1'),
      otherwise: schema => schema,
    })
    .typeError('Must be a number')
    .integer('Must be an integer')
    .max(10000000, 'Amount cannot exceed 1 crore'),

  flat_fee: Yup.number()
    .when('flat_fee_box', {
      is: true,
      then: schema =>
        schema.required('Flat fee is required').min(1, 'Must be at least 1'),
      otherwise: schema => schema,
    })
    .typeError('Must be a number')
    .integer('Must be an integer')
    .max(10000000, 'Amount cannot exceed 1 crore'),

  hourly_rate: Yup.number()
    .when('hourly_box', {
      is: true,
      then: schema =>
        schema.required('Hourly rate is required').min(1, 'Must be at least 1'),
      otherwise: schema => schema,
    })
    .typeError('Must be a number')
    .integer('Must be an integer')
    .max(10000000, 'Amount cannot exceed 1 crore'),
});
export const BankDetailsForm = memo(
  forwardRef((props: Props, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t, i18n } = useTranslation();
    const formikRef = useRef<any>(null);
    const { advisorData, setTrackSubmission } = useAdvisor();
    // Expose the submitForm function to the parent
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formikRef.current?.handleSubmit();
      },
    }));
    return (
      <Div>
        <Formik
          innerRef={formikRef} // Assign the formik ref here
          initialValues={{
            charge_free: advisorData.charges.free_consultancy,
            charge_aum: advisorData.charges.aum_based,
            fee_percentage: advisorData.charges.fee_percentage,
            minimum_fee: advisorData.charges.minimum_fee,
            flat_fee_box: advisorData.charges.flat_fee,
            flat_fee: advisorData.charges.flat_fee_value,
            hourly_box: advisorData.charges.hourly_rate,
            hourly_rate: advisorData.charges.hourly_rate_value,
            anyOptionSelected: true, // This is a virtual field for validation
          }}
          validationSchema={validationSchema}
          onSubmit={values => {
            setTrackSubmission('professionalDetails', true);
            props.onsubmit(values);
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleChange,
            handleBlur,
          }) => (
            <Form>
              <Text>
                Choose all the applicable revenue models that you prefer
              </Text>
              <div style={{ borderBottom: '1px solid #E4E6EC' }}>
                <FieldWrapper
                  className="d-flex"
                  style={{ gap: '15px' }}
                  freeLabel
                >
                  <StyledCheckbox
                    type="checkbox"
                    name="charge_free"
                    checked={values.charge_free}
                    onChange={handleChange}
                  />
                  <Textsmall className="d-flex align-items-center">
                    Free Consultation for first 30 minutes
                  </Textsmall>
                </FieldWrapper>
              </div>
              <InputWrapper style={{ borderBottom: '1px solid #E4E6EC' }}>
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '15px' }}
                >
                  <div className="d-flex align-items-center">
                    <StyledCheckbox
                      type="checkbox"
                      name="charge_aum"
                      checked={values.charge_aum}
                      onChange={handleChange}
                    />
                  </div>
                  <Text style={{ marginTop: '0px' }}>AUM-based Fee</Text>
                </div>
                <FieldWrapper className="d-flex flex-column w-100">
                  <DoubleWrapper>
                    <LabelWrapper className="d-flex flex-column w-100">
                      <Label>Fee percentage</Label>
                      <Cover hasError={errors.fee_percentage}>
                        <StyledInput
                          name="fee_percentage"
                          type="number"
                          onChange={handleChange}
                          autoComplete="off"
                          placeholder="0"
                          value={values.fee_percentage}
                          onBlur={handleBlur}
                          width="100%"
                        />
                        <img
                          style={{
                            width: '15px',
                            height: '22px',
                            display: 'flex',
                            alignSelf: 'center',
                          }}
                          src={percentage}
                          alt=""
                        />
                        {errors.fee_percentage && touched.fee_percentage && (
                          <Error className="ms-1">
                            {errors.fee_percentage}
                          </Error>
                        )}
                      </Cover>
                    </LabelWrapper>
                    <LabelWrapper className="d-flex flex-column w-100">
                      <Label>Minimum Fees</Label>
                      <Cover hasError={errors.minimum_fee}>
                        <img
                          style={{
                            width: '10px',
                            height: '22px',
                            display: 'flex',
                            alignSelf: 'center',
                          }}
                          src={rupee}
                          alt=""
                        />
                        <StyledInput
                          name="minimum_fee"
                          type="number"
                          placeholder="0"
                          onChange={handleChange}
                          autoComplete="off"
                          value={values.minimum_fee}
                          onBlur={handleBlur}
                          width="100%"
                        />
                        {errors.minimum_fee && touched.minimum_fee && (
                          <Error className="ms-1">{errors.minimum_fee}</Error>
                        )}
                      </Cover>
                    </LabelWrapper>
                  </DoubleWrapper>
                  <Default className="w-100">
                    Enter default values, but you can change them anytime from
                    settings
                  </Default>
                </FieldWrapper>
              </InputWrapper>
              <InputWrapper style={{ borderBottom: '1px solid #E4E6EC' }}>
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '15px' }}
                >
                  <div className="d-flex align-items-center">
                    <StyledCheckbox
                      type="checkbox"
                      name="hourly_box"
                      checked={values.hourly_box}
                      onChange={handleChange}
                    />
                  </div>
                  <Text style={{ marginTop: '0px' }}>Hourly Rate</Text>
                </div>
                <FieldWrapper className="d-flex flex-column w-100">
                  <LabelWrapper className="d-flex flex-column w-50">
                    <Label>Fee Amount</Label>
                    <Cover hasError={errors.hourly_rate}>
                      <img
                        style={{
                          width: '10px',
                          height: '22px',
                          display: 'flex',
                          alignSelf: 'center',
                        }}
                        src={rupee}
                        alt=""
                      />
                      <StyledInput
                        name="hourly_rate"
                        type="number"
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="0"
                        value={values.hourly_rate}
                        onBlur={handleBlur}
                        width="100%"
                      />
                      {errors.hourly_rate && touched.hourly_rate && (
                        <Error className="ms-1">{errors.hourly_rate}</Error>
                      )}
                    </Cover>
                  </LabelWrapper>
                  <Default className="w-100">
                    Enter default values, but you can change them anytime from
                    settings
                  </Default>
                </FieldWrapper>
              </InputWrapper>
              <InputWrapper>
                <div
                  className="d-flex align-items-center"
                  style={{ gap: '15px' }}
                >
                  <div className="d-flex align-items-center">
                    <StyledCheckbox
                      type="checkbox"
                      name="flat_fee_box"
                      checked={values.flat_fee_box}
                      onChange={handleChange}
                    />
                  </div>
                  <Text style={{ marginTop: '0px' }}>Flat Fee</Text>
                </div>
                <FieldWrapper className="d-flex flex-column w-100">
                  <LabelWrapper className="d-flex flex-column w-50">
                    <Label>Fee Amount</Label>
                    <Cover hasError={errors.flat_fee}>
                      <img
                        style={{
                          width: '10px',
                          height: '22px',
                          display: 'flex',
                          alignSelf: 'center',
                        }}
                        src={rupee}
                        alt=""
                      />
                      <StyledInput
                        className=""
                        name="flat_fee"
                        type="number"
                        onChange={handleChange}
                        placeholder="0"
                        autoComplete="off"
                        value={values.flat_fee}
                        onBlur={handleBlur}
                        width="100%"
                      />
                      {errors.flat_fee && touched.flat_fee && (
                        <Error className="ms-1">{errors.flat_fee}</Error>
                      )}
                    </Cover>
                  </LabelWrapper>
                  <Default className="w-100">
                    Enter default values, but you can change them anytime from
                    settings
                  </Default>
                </FieldWrapper>
              </InputWrapper>
              <div className="d-flex justify-content-end flex-column align-items-end">
                {errors.anyOptionSelected && (
                  <div
                    className="text-danger mb-2"
                    style={{ fontSize: '12px', fontWeight: '500' }}
                  >
                    {errors.anyOptionSelected}
                  </div>
                )}
                <Button type="submit">Continue</Button>
              </div>
            </Form>
          )}
        </Formik>
      </Div>
    );
  }),
);

const Textsmall = styled.h6`
  font-weight: 500;
  font-size: 16px;
  text-align: left;
  margin: 0;
  ${media.small} {
    font-size: 14px;
    font-weight: 500;
  }
`;
const Default = styled.h6`
  font-weight: 300;
  font-size: 12px;
  text-align: left;
  margin: 0;
`;
const Cover = styled.div<{ width?: string }>`
  display: flex;
  position: relative;
  border: 1px solid ${props => (props.hasError ? '#FFC6BF' : ' #c8ccd8')};
  // border: 1px solid #c8ccd8;
  border-radius: 10px;
  width: 100%;
  ${(props: props) =>
    props.width &&
    `
        width: ${props.width};
    `}
  height:40px;
  padding: 10px 15px;
  gap: 10px;
`;
const StyledInput = styled.input<{ width?: string; Placeholder?: boolean }>`
  font-size: 1rem;
  width: 100%;
  outline: none;
  border: none;
  text-align: left;
  ${(props: props) =>
    props.width &&
    `
        width: ${props.width};
    `}
  &::placeholder {
    text-align: left;
    font-size: 14px;
    font-weight: 300;
  }
  /* Hide increment/decrement arrows for number inputs */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* For Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const Label = styled.h6`
  font-weight: 700;
  font-size: 14px;
  margin: 0;
  ${media.small} {
    font-weight: 600;
  }
`;
const Div = styled.div`
  width: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  height: 100%;
`;
export interface props {
  width?: string;
}
const StyledCheckbox = styled.input`
  &[type='checkbox'] {
    display: inline-block;
    width: 32px;
    height: 32px;
    border: 1.5px solid #8276f4;
    border-radius: 50%; /* Square with rounded edges */
    background-color: white;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    appearance: none;
    outline: none;
    ${media.large} {
      width: 21px;
      height: 21px;
    }
    ${media.xlarge} {
      width: 21px;
      height: 21px;
    }
  }
  &:checked {
    background-color: #8276f4;
    border-color: #8276f4;
  }

  &:checked::after {
    content: '';
    width: 5px; /* Width of the checkmark lines */
    height: 9.5px; /* Height of the checkmark lines */
    border: solid white;
    border-width: 0 1.5px 1.5px 0; /* Right and bottom borders */
    position: absolute;
    top: 45%;
    left: 31%;
    transform: translate(-50%, -50%) rotate(45deg); /* Rotate to form the "V" shape */
    transform-origin: bottom right;
  }
`;

const Text = styled.div`
  width: 100%;
  display: flex;
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  ${media.small} {
    font-size: 14px;
    font-weight: 500;
  }
`;
const Error = styled.div`
  color: #ff7b6a;
  font-weight: 400;
  font-size: 10px;
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 1px;
  white-space: nowrap;
  max-width: 100%;
  line-height: 1.2;
`;
const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  & > :first-child {
    flex: 0 0 30%;
  }
  & > :last-child {
    flex: 0 0 70%;
  }
  ${media.small} {
    flex-direction: column;
    margin-top: 15px;
    & > :first-child,
    & > :last-child {
      flex: 0 0 100%;
    }
  }
`;
const FieldWrapper = styled.div<{ freeLabel: boolean }>`
  gap: ${prop => (prop.freeLabel ? '15px' : '10px')};
  margin-top: 30px;
  margin-bottom: ${prop => (prop.freeLabel ? '20px' : '30px')};
  ${media.small} {
    margin-top: ${prop => (prop.freeLabel ? '15px' : '20px')};
    margin-bottom: ${prop => (prop.freeLabel ? '15px' : '20px')};
  }
`;
const LabelWrapper = styled.div`
  gap: 20px;
  ${media.small} {
    gap: 10px;
  }
`;
const DoubleWrapper = styled.div`
  display: flex;
  gap: 10px;
  box-sizing: border-box;
  & > :first-child {
    flex: 0 0 30%;
  }
  & > :last-child {
    flex: 0 0 50%;
  }
  ${media.small} {
    & > :first-child {
      flex: 0 0 50%;
    }
    & > :last-child {
      flex: 0 0 50%;
    }
  }
`;
const Button = styled.button`
  height: 52px;
  background-color: #8276f4;
  padding: 15px 30px;
  border-radius: 30px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin-top: 20px;
  border: none;
  &:hover {
    background-color: #5c54ad;
  }
  ${media.small} {
    margin-top: 20px;
  }
  ${media.medium} {
    position: absolute;
    bottom: 20px;
  }
`;
